import React, { useContext, useEffect, useRef, useState } from "react";
import {
  chordsForProgression,
  audioSupported,
  midiToKey,
  startGroove,
  audioNow,
  playClick,
  playNote,
  COMP_PATTERNS,
} from "../../lib/keyboardPlan";
import {
  degStr,
  chordFor,
  chordRecipe,
  chordLabel,
  questionConcept,
  gradeStep,
  buildHint,
  beatMsToBpm,
  TIMED_PASSES,
  timedBarResult,
  timedRunSummary,
  timedCompBarResult,
  timedCompRunSummary,
  timedRestBarResult,
  shellConceptId,
  REST,
  earSlotGrade,
  earSlotResults,
  playedTravel,
  VOICELEAD_SLACK,
  lessonConceptPairs,
  conceptsThroughLesson,
  questionConcepts,
  lessonProgressions,
} from "../../lib/numberSenseData";
import {
  Icon,
  Html,
  Piano,
  PlayButton,
  EarSlots,
  MidiBadge,
  TypingHint,
  useTypingPiano,
  AudioSettingsContext,
} from "../numberSenseUi";
import useMidi from "./useMidi";

/* ============================================================
   Number Sense — the lesson runtime (one self-contained round of
   questions with hearts). It owns the transient gameplay state and
   reports the outcome back through onFinish; the page owns
   persistence (XP / streak / mastery / history).

   Mount it with a key that changes per attempt (e.g. a run counter)
   so retrying a lesson resets state via a fresh mount.

   Props:
     lesson   : { questions:[..], review?, unit, idx, id }
     onClose  : () => void                     bail out to the path
     onFinish : (passed, { hearts, correct, total, results }) => void
   ============================================================ */
const CAN_PLAY = audioSupported();

// The popup's fixed width (kept in sync with .ns-chip-popup in numbersense.css)
// so it can be clamped on-screen at the app's ~375–520px widths.
const CHIP_POPUP_WIDTH = 232;

// A tappable chord reference — wraps a chord symbol/label wherever one shows
// up in a question prompt so the learner can check it mid-lesson without
// leaving the question. `info` is the popup content ({ symbol, label, notes })
// precomputed by the caller (chordRecipe for a plain (key, deg), or the
// question's own fields for a voicing/inversion that isn't root position).
// `id` identifies this chip so the shared popup can toggle closed on a re-tap.
function ChordChip({
  id,
  info,
  activeId,
  onToggle,
  children,
  className = "ns-chip",
  emptyTag = null,
}) {
  const ref = useRef(null);
  if (!info) {
    // Nothing to show — render plain text inline (build/inversion/voicelead
    // prompts), or keep the caller's wrapper element + classes so a card
    // (e.g. a rest bar in the progression chart) doesn't lose its layout.
    if (!emptyTag) return <>{children}</>;
    const Tag = emptyTag;
    return <Tag className={className}>{children}</Tag>;
  }
  const open = activeId === id;
  return (
    <button
      type="button"
      ref={ref}
      className={`${className}${open ? " open" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(
          open
            ? null
            : { id, ...info, rect: ref.current.getBoundingClientRect() }
        );
      }}
    >
      {children}
    </button>
  );
}

// The single shared popup a ChordChip opens: chord symbol, full label, notes,
// and a PlayButton to hear it. Hand-rolled fixed positioning anchored under
// the tapped chip, clamped so it stays on-screen down to a ~375px viewport.
function ChordPopup({ info, onClose }) {
  if (!info) return null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 375;
  const left = Math.min(Math.max(8, info.rect.left), vw - CHIP_POPUP_WIDTH - 8);
  const top = info.rect.bottom + 8;
  return (
    <>
      <div className="ns-chip-backdrop" onClick={onClose} />
      <div
        className="ns-chip-popup"
        style={{ left, top }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="ns-chip-popup-close"
          onClick={onClose}
          aria-label="close"
        >
          ×
        </button>
        <div className="ns-chip-popup-sym mono">{info.symbol}</div>
        {info.label && <div className="ns-chip-popup-label">{info.label}</div>}
        {info.notes && info.notes.length > 0 && (
          <div className="ns-chip-popup-notes mono">{info.notes.join(" – ")}</div>
        )}
        {info.notes && info.notes.length > 0 && (
          <PlayButton chords={[info.notes]} countIn={false} label="hear it" />
        )}
      </div>
    </>
  );
}

// chordRecipe-backed popup info for a plain (key, deg) concept — the common
// case (build / inversion / play-the-progression steps).
const conceptChipInfo = (key, deg) => {
  const r = chordRecipe(key, deg);
  if (!r) return null;
  return { symbol: r.chord, label: r.label, notes: r.notes };
};

// The learned-so-far reference panel's content for one lesson. Curriculum
// lessons (a real unit + numeric idx) read lessonConceptPairs /
// conceptsThroughLesson; capstones (idx "cap") roll up everything the whole
// unit taught; review/daily/drill lessons (no unit) fall back to whatever
// (key, deg) pairs the lesson's own questions touch.
function panelConcepts(lesson) {
  const unit = lesson && lesson.unit;
  const numericIdx = typeof (lesson && lesson.idx) === "number";
  let fresh = numericIdx ? lessonConceptPairs(unit, lesson.idx) : [];
  let earlier = unit
    ? conceptsThroughLesson(unit, numericIdx ? lesson.idx : (unit.plan || []).length)
    : [];
  let fallback = false;
  if (!fresh.length && !earlier.length) {
    const derived = questionConcepts(lesson ? lesson.questions : []);
    if (derived.length) {
      fresh = derived;
      fallback = true;
    }
  }
  const progressions = lessonProgressions(lesson ? lesson.questions : []);
  return { fresh, earlier, fallback, progressions };
}

// The "learned so far" slide-over: what this lesson (and the unit before it)
// has taught, always freely browsable — no leaderboards here, knowledge is
// the whole point. Backdrop tap / Escape / the × closes it.
function LearnedPanel({ lesson, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { fresh, earlier, fallback, progressions } = panelConcepts(lesson);
  const nothing = !fresh.length && !earlier.length && !progressions.length;

  const conceptRow = ({ key, deg }) => {
    const r = chordRecipe(key, deg);
    if (!r) return null;
    return (
      <div className="ns-learned-row" key={`${key}:${deg}`}>
        <div className="ns-learned-row-head">
          <span className="mono num">{deg}</span>
          <span className="chord">{r.chord}</span>
        </div>
        <div className="ns-learned-row-notes muted mono">{r.notes.join(" – ")}</div>
        <PlayButton chords={[r.notes]} countIn={false} label="hear it" />
      </div>
    );
  };

  return (
    <>
      <div className="ns-panel-backdrop" onClick={onClose} />
      <div className="ns-panel" role="dialog" aria-label="learned so far">
        <div className="ns-panel-head">
          <h2>learned so far</h2>
          <button
            type="button"
            className="ns-panel-close"
            onClick={onClose}
            aria-label="close"
          >
            ×
          </button>
        </div>
        <div className="ns-panel-body">
          {nothing && (
            <p className="ns-panel-empty muted">
              nothing to review yet — chords will show up here as the lesson
              introduces them.
            </p>
          )}
          {fresh.length > 0 && (
            <div className="ns-panel-sec">
              <h3>{fallback ? "chords in this lesson" : "new this lesson"}</h3>
              <div className="ns-learned-list">{fresh.map(conceptRow)}</div>
            </div>
          )}
          {earlier.length > 0 && (
            <div className="ns-panel-sec">
              <h3>earlier in this unit</h3>
              <div className="ns-learned-list">{earlier.map(conceptRow)}</div>
            </div>
          )}
          {progressions.length > 0 && (
            <div className="ns-panel-sec">
              <h3>progressions practiced this lesson</h3>
              <div className="ns-learned-list">
                {progressions.map((p, i) => (
                  <div className="ns-learned-row" key={i}>
                    <div className="ns-learned-row-head">
                      <span className="chord">
                        {p.key}: {p.labels ? p.labels.join(" – ") : degStr(p.degrees)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const LessonScreen = ({ lesson, onClose, onFinish }) => {
  const [qi, setQi] = useState(0);
  const [hearts, setHearts] = useState(5);
  const [correct, setCorrect] = useState(0);
  const [total, setTotal] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(false);
  const [selection, setSelection] = useState(null);
  const [built, setBuilt] = useState(() => new Set()); // pressed pitch-classes
  const [showHint, setShowHint] = useState(false); // "how do I build this?" reveal
  const [invKeys, setInvKeys] = useState(() => new Set()); // pressed MIDI notes (octave-aware, for inversions)
  const [playStep, setPlayStep] = useState(0); // play-the-progression chord index
  const [earPicks, setEarPicks] = useState([]); // slot-by-slot ear answers
  const [earVerdict, setEarVerdict] = useState(null); // earSlotGrade result
  const [leadInfo, setLeadInfo] = useState(null); // voice-lead travel verdict
  const [chipPopup, setChipPopup] = useState(null); // the open tap-a-chord popup, if any
  const [showLearned, setShowLearned] = useState(false); // "learned so far" slide-over
  const resultsRef = useRef([]); // { concept, correct, ms, type } per graded question

  // Escape closes whichever overlay is open (the popup takes priority — it's
  // the more transient one).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (chipPopup) setChipPopup(null);
      else if (showLearned) setShowLearned(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chipPopup, showLearned]);

  // ---- timed play-the-changes (playprog with q.timed) ----
  const [timedPhase, setTimedPhase] = useState("ready"); // ready | play
  const [timedBar, setTimedBar] = useState(-1); // absolute bar index while playing
  const [marks, setMarks] = useState([]); // closed bars: { hit, onTime, latency }
  const [timedSummary, setTimedSummary] = useState(null);
  const marksRef = useRef([]);
  const strikeRef = useRef([]); // { name, t } note-ons on the audio clock
  const grooveRef = useRef(null);
  const countInRef = useRef(null);
  const audioCfg = useContext(AudioSettingsContext);

  // When the current question was shown — response time feeds fluency mastery.
  const qStartRef = useRef(Date.now());

  const stopGroove = () => {
    if (countInRef.current) {
      clearTimeout(countInRef.current);
      countInRef.current = null;
    }
    if (grooveRef.current) {
      grooveRef.current.stop();
      grooveRef.current = null;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopGroove(), []);

  const grade = (isCorrect) => {
    const question = lesson.questions[qi];
    const concept = questionConcept(question);
    if (concept)
      resultsRef.current.push({
        concept,
        correct: isCorrect,
        ms: Date.now() - qStartRef.current,
        type: question.type,
      });
    const next = gradeStep({ total, correct, hearts }, isCorrect);
    setAnswered(true);
    setLastCorrect(isCorrect);
    setTotal(next.total);
    setCorrect(next.correct);
    setHearts(next.hearts);
    if (next.hearts <= 0) {
      setTimeout(
        () =>
          onFinish(false, {
            hearts: next.hearts,
            correct: next.correct,
            total: next.total,
            results: resultsRef.current,
            qi, // where the run died — the done screen can restart from here
          }),
        500
      );
    }
  };

  const advance = () => {
    stopGroove();
    const nextQi = qi + 1;
    setAnswered(false);
    setSelection(null);
    setBuilt(new Set());
    setInvKeys(new Set());
    setPlayStep(0);
    setEarPicks([]);
    setEarVerdict(null);
    setLeadInfo(null);
    setShowHint(false);
    setChipPopup(null);
    setTimedPhase("ready");
    setTimedBar(-1);
    setMarks([]);
    setTimedSummary(null);
    marksRef.current = [];
    strikeRef.current = [];
    qStartRef.current = Date.now();
    if (nextQi >= lesson.questions.length) {
      onFinish(true, { hearts, correct, total, results: resultsRef.current, qi });
      return;
    }
    setQi(nextQi);
  };

  // A slipped groove take shouldn't be one-and-done: the failed take already
  // cost its heart, so while hearts remain the player can reset this question
  // to "ready" and take it again (each further slip costs another heart —
  // hearts are the retry budget).
  const retryTake = () => {
    stopGroove();
    setAnswered(false);
    setBuilt(new Set());
    setTimedPhase("ready");
    setTimedBar(-1);
    setMarks([]);
    setTimedSummary(null);
    marksRef.current = [];
    strikeRef.current = [];
    qStartRef.current = Date.now();
  };

  const q = lesson.questions[qi];
  const pct = (qi / lesson.questions.length) * 100;

  // Marks the unit's closing "put it together" synthesis phase (see
  // buildCapstone) so the shift from drilling to applying reads on screen.
  const capstoneTag = q.capstone ? (
    <div className="ns-capstone-tag">
      <Icon name="trophy" />
      <span>put it together</span>
    </div>
  ) : null;

  // Toggle a pitch-class in the build set — shared by screen taps and MIDI.
  const toggleBuilt = (name) =>
    setBuilt((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Toggle an exact MIDI note — inversions care which octave is on the bottom.
  const toggleInv = (m) =>
    setInvKeys((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });

  const isTimed = q.type === "playprog" && Boolean(q.timed);
  // a timed comp run (#95): shells on a comping pattern, graded notes × rhythm
  const isComp = isTimed && Boolean(q.voicing) && Boolean(q.pattern);
  const compPattern = isComp ? COMP_PATTERNS[q.pattern] || COMP_PATTERNS.pads : null;
  const loopLen = q.type === "playprog" ? q.degrees.length : 0;
  const totalBars = isTimed ? loopLen * TIMED_PASSES : 0;

  // One collector for every input path (MIDI, typing keyboard, glass piano):
  // a timed run records strikes on the audio clock; everything else feeds the
  // pitch-class / exact-key sets the graders read.
  const handleKeyInput = (k) => {
    if (answered) return;
    if (isTimed) {
      if (timedPhase !== "play") return;
      strikeRef.current.push({ name: k.name, t: audioNow() });
      toggleBuilt(k.name); // visual feedback only — strikes drive the grading
    } else if (q.type === "build" || q.type === "playprog") toggleBuilt(k.name);
    else if (q.type === "inversion" || q.type === "voicelead") toggleInv(k.midi);
  };

  // A real MIDI keyboard feeds the right collector for the active question.
  const midi = useMidi(handleKeyInput);

  // …and so does the typing keyboard (A S D F… — see KEYBOARD_TO_MIDI), which
  // makes *playing* answers possible without any hardware. It sounds the note
  // itself; MIDI keyboards and the glass piano already make their own sound.
  useTypingPiano(
    (k) => {
      playNote(k.midi);
      handleKeyInput(k);
    },
    q.type === "build" ||
      q.type === "playprog" ||
      q.type === "inversion" ||
      q.type === "voicelead"
  );

  // Play-the-progression (untimed): when the played notes match the current
  // chord, advance to the next; completing the loop passes the question.
  const playTarget =
    q.type === "playprog" && !isTimed ? q.steps[playStep] || [] : null;
  useEffect(() => {
    if (q.type !== "playprog" || q.timed || answered || !playTarget || !playTarget.length)
      return;
    const hit =
      built.size === playTarget.length && playTarget.every((n) => built.has(n));
    if (!hit) return;
    const nextStep = playStep + 1;
    setBuilt(new Set());
    if (nextStep >= q.steps.length) grade(true);
    else setPlayStep(nextStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, playStep, q, answered]);

  // ---- timed play-the-changes transport ----
  // One bar of count-in clicks, then the groove runs TIMED_PASSES times around
  // the loop. Bars are closed against the audio clock; each closed bar is
  // graded by timedBarResult and the run ends in one grade() call.
  // One pass of the pattern comped by the app (rootless, over the groove) so
  // the feel can be heard before it has to be played. Ready phase only.
  const previewPattern = () => {
    stopGroove();
    const beatMs = audioCfg.tempo;
    grooveRef.current = startGroove({
      key: q.key,
      degrees: q.degrees,
      bpm: beatMsToBpm(beatMs),
      pattern: q.pattern,
      comp: true,
      rootless: true,
    });
    countInRef.current = window.setTimeout(() => {
      countInRef.current = null;
      stopGroove();
    }, loopLen * 4 * beatMs + 200);
  };

  const startTimed = () => {
    if (timedPhase === "play") return;
    stopGroove(); // a running pattern preview yields to the real take
    strikeRef.current = [];
    marksRef.current = [];
    setMarks([]);
    setBuilt(new Set());
    setTimedBar(-1);
    setTimedPhase("play");
    const beatMs = audioCfg.tempo;
    for (let i = 0; i < 4; i += 1) playClick((i * beatMs) / 1000, i === 0);
    countInRef.current = window.setTimeout(() => {
      countInRef.current = null;
      grooveRef.current = startGroove({
        key: q.key,
        degrees: q.degrees,
        bpm: beatMsToBpm(beatMs),
      });
    }, 4 * beatMs);
  };

  useEffect(() => {
    if (!isTimed || timedPhase !== "play" || answered) return undefined;
    const id = setInterval(() => {
      const g = grooveRef.current;
      if (!g) return; // still counting in
      const el = audioNow() - g.startTime;
      if (el < 0) return;
      const beatSec = g.barDur / 4;
      const bar = Math.min(Math.floor(el / g.barDur), totalBars);
      let closedAny = false;
      while (marksRef.current.length < bar) {
        const i = marksRef.current.length;
        const start = g.startTime + i * g.barDur;
        const end = start + g.barDur;
        // a strike slightly ahead of the downbeat still belongs to this bar
        const strikes = strikeRef.current.filter(
          (s) => s.t >= start - beatSec / 2 && s.t < end
        );
        marksRef.current.push(
          q.degrees[i % loopLen] === REST
            ? // a rest bar: silence is the answer (strikes before the downbeat
              // belong to the previous bar — the grader ignores them)
              timedRestBarResult(strikes, start)
            : isComp
            ? timedCompBarResult(
                strikes,
                q.steps[i % loopLen],
                compPattern.hits,
                start,
                beatSec
              )
            : timedBarResult(strikes, q.steps[i % loopLen], start, beatSec)
        );
        closedAny = true;
      }
      if (closedAny) {
        setMarks([...marksRef.current]);
        setBuilt(new Set()); // fresh visual for the new bar
      }
      if (bar >= totalBars) {
        stopGroove();
        const summary = isComp
          ? timedCompRunSummary(marksRef.current)
          : timedRunSummary(marksRef.current);
        setTimedSummary(summary);
        // each bar exercised its chord's concept — hit counts as correct,
        // on-the-beat counts as fluent. Comp bars target the shell, so they
        // feed the shell dimension; rest bars exercise restraint, not a chord
        marksRef.current.forEach((b, i) => {
          const deg = q.degrees[i % loopLen];
          if (deg === REST) return;
          resultsRef.current.push({
            concept: isComp
              ? shellConceptId(q.key, deg)
              : `${q.key}:${deg}`,
            correct: b.hit,
            fast: b.onTime,
            ms: b.latency != null ? Math.round(b.latency * 1000) : null,
            type: "playprog",
          });
        });
        grade(summary.passed);
        return;
      }
      setTimedBar(bar);
    }, 60);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimed, timedPhase, answered, qi]);

  const heartsMini = (
    <div className="hearts-mini">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} style={{ opacity: i < hearts ? 1 : 0.25 }}>
          <Icon name="heart" />
        </span>
      ))}
    </div>
  );

  const lessonBar = (
    <>
      <div className="lessonbar">
        <button className="close" onClick={onClose}>
          ×
        </button>
        <div className="progress">
          <i style={{ width: `${pct}%` }} />
        </div>
        {heartsMini}
        <button
          type="button"
          className="iconbtn learned-btn"
          onClick={() => setShowLearned(true)}
          aria-label="what you've learned so far"
        >
          <Icon name="book" />
        </button>
      </div>
      <ChordPopup info={chipPopup} onClose={() => setChipPopup(null)} />
      {showLearned && (
        <LearnedPanel lesson={lesson} onClose={() => setShowLearned(false)} />
      )}
    </>
  );

  // ---- build question: its own piano interaction ----
  if (q.type === "build") {
    const want = new Set(q.targetNotes);
    const press = (k) => {
      if (answered) return;
      toggleBuilt(k.name);
    };
    const checkBuild = () => {
      const ok = built.size === want.size && [...want].every((n) => built.has(n));
      grade(ok);
    };
    return (
      <div className="ns-root">
        <div className="ns-app">
          {lessonBar}
          <div className="qarea">
            <div className="qkind">
              {q.scale
                ? "build the scale"
                : q.color
                ? "build the color"
                : q.voicing
                ? "voice the chord"
                : "build the chord"}
            </div>
            <div className="qprompt">
              play{" "}
              <ChordChip
                id={`build-${q.key}-${q.deg}`}
                info={
                  !q.scale && !q.color && !q.voicing
                    ? conceptChipInfo(q.key, q.deg)
                    : null
                }
                activeId={chipPopup && chipPopup.id}
                onToggle={setChipPopup}
              >
                <b>{q.label}</b>{" "}
                {!q.scale && (
                  <span className="muted">
                    ({q.key} · {q.deg})
                  </span>
                )}
              </ChordChip>
            </div>
            {q.scale && (
              <div className="qprompt-sub muted">all seven notes — any octave</div>
            )}
            {q.voicing && (
              <div className="qprompt-sub muted">
                guide tones — skip the root, the bass has it
              </div>
            )}
            {!answered && (
              <div className="ns-hint">
                {showHint ? (
                  <p className="ns-hint-text">{buildHint(q)}</p>
                ) : (
                  <button
                    type="button"
                    className="ns-hint-btn"
                    onClick={() => setShowHint(true)}
                  >
                    how do I build this?
                  </button>
                )}
              </div>
            )}
            <Piano
              pressed={built}
              onPress={press}
              targetNotes={q.targetNotes}
              result={answered ? (lastCorrect ? "right" : "wrong") : null}
              disabled={answered}
            />
            <div className="ns-built">
              {built.size ? [...built].join(" · ") : "tap the notes (any octave)…"}
            </div>
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {!answered ? (
            <div className="foot">
              <button
                className="btn"
                disabled={
                  built.size <
                  (q.scale ? 7 : q.color ? q.targetNotes.length : q.voicing ? 2 : 3)
                }
                onClick={checkBuild}
              >
                Check
              </button>
            </div>
          ) : (
            <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
              <div className="verdict">
                <b>{lastCorrect ? "nailed it" : "not quite"}</b>
                <small>
                  {lastCorrect
                    ? q.scale
                      ? "that's the scale"
                      : "that's the chord"
                    : `${q.label} is ${q.targetNotes.join("–")}`}
                </small>
              </div>
              <button
                className={`btn ${lastCorrect ? "" : "loje"}`}
                onClick={advance}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- inversion question: play the triad with a specific note in the bass ----
  if (q.type === "inversion") {
    const want = new Set(q.notes);
    const keysList = [...invKeys];
    const exactKeys = new Set(
      keysList.map((m) => {
        const kk = midiToKey(m);
        return `${kk.name}${kk.octave}`;
      })
    );
    const press = (k) => {
      if (!answered) toggleInv(k.midi);
    };
    const checkInv = () => {
      const pcs = new Set(keysList.map((m) => midiToKey(m).name));
      const lowest = Math.min(...keysList);
      const bassName = midiToKey(lowest).name;
      const ok =
        pcs.size === want.size &&
        [...want].every((n) => pcs.has(n)) &&
        bassName === q.bass;
      grade(ok);
    };
    return (
      <div className="ns-root">
        <div className="ns-app">
          {lessonBar}
          <div className="qarea">
            <div className="qkind">put the bass note on the bottom</div>
            <div className="qprompt">
              play{" "}
              <ChordChip
                id={`inv-${q.key}-${q.deg}`}
                info={conceptChipInfo(q.key, q.deg)}
                activeId={chipPopup && chipPopup.id}
                onToggle={setChipPopup}
              >
                <b>{q.name}</b>{" "}
                <span className="muted">
                  ({q.key} · {q.deg})
                </span>
              </ChordChip>
            </div>
            <div className="qprompt-sub muted">
              all three notes, with <b>{q.bass}</b> as the lowest you press
            </div>
            {CAN_PLAY && (
              <div className="ns-listen">
                <PlayButton chords={[q.voiced]} countIn={false} label="hear it" />
              </div>
            )}
            <Piano
              pressed={built}
              exactKeys={exactKeys}
              onPress={press}
              hint={answered ? [] : q.notes}
              disabled={answered}
            />
            <div className="ns-built">
              {keysList.length
                ? keysList
                    .sort((a, b) => a - b)
                    .map((m) => midiToKey(m).name)
                    .join(" · ")
                : "tap the notes — lowest first…"}
            </div>
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {!answered ? (
            <div className="foot">
              <button className="btn" disabled={invKeys.size < 3} onClick={checkInv}>
                Check
              </button>
            </div>
          ) : (
            <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
              <div className="verdict">
                <b>{lastCorrect ? "smooth" : "not quite"}</b>
                <small>
                  {lastCorrect
                    ? `${q.name} — ${q.voiced.join("–")}`
                    : `${q.name} needs ${q.bass} in the bass (${q.voiced.join("–")})`}
                </small>
              </div>
              <button
                className={`btn ${lastCorrect ? "" : "loje"}`}
                onClick={advance}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- voice-lead question: any inversion is right — the travel is scored ----
  if (q.type === "voicelead") {
    const want = new Set(q.notes);
    const keysList = [...invKeys];
    const exactKeys = new Set(
      keysList.map((m) => {
        const kk = midiToKey(m);
        return `${kk.name}${kk.octave}`;
      })
    );
    const press = (k) => {
      if (!answered) toggleInv(k.midi);
    };
    const checkLead = () => {
      const pcs = new Set(keysList.map((m) => midiToKey(m).name));
      const ok = pcs.size === want.size && [...want].every((n) => pcs.has(n));
      const travel = ok ? playedTravel(q.prev, keysList) : null;
      const smooth =
        ok && travel != null && travel <= q.bestCost + VOICELEAD_SLACK;
      setLeadInfo({ travel, smooth });
      // economy is this question's fluency: right + short path promotes,
      // right-but-wide holds the box (the explicit fast flag wins over speed)
      resultsRef.current.push({
        concept: `${q.key}:${q.deg}`,
        correct: ok,
        fast: smooth,
        ms: Date.now() - qStartRef.current,
        type: "voicelead",
      });
      grade(ok);
    };
    return (
      <div className="ns-root">
        <div className="ns-app">
          {lessonBar}
          <div className="qarea">
            {capstoneTag}
            <div className="qkind">voice-lead the change</div>
            <div className="qprompt">
              move to{" "}
              <ChordChip
                id={`lead-target-${q.key}-${q.deg}`}
                info={{ symbol: q.chord, label: chordLabel(q.chord), notes: q.notes }}
                activeId={chipPopup && chipPopup.id}
                onToggle={setChipPopup}
              >
                <b>{q.label}</b>{" "}
                <span className="muted">
                  ({q.key} · {q.deg})
                </span>
              </ChordChip>
            </div>
            <div className="qprompt-sub muted">
              your hand is on{" "}
              <ChordChip
                id={`lead-prev-${q.key}-${q.deg}`}
                info={
                  q.prevLabel
                    ? { symbol: q.prevLabel, label: chordLabel(q.prevLabel), notes: q.prev }
                    : null
                }
                activeId={chipPopup && chipPopup.id}
                onToggle={setChipPopup}
              >
                <b>
                  {q.prevLabel ? `${q.prevLabel}: ` : ""}
                  {q.prev.join("–")}
                </b>
              </ChordChip>{" "}
              — any way up counts, the smallest move wins
            </div>
            {CAN_PLAY && (
              <div className="ns-listen">
                <PlayButton
                  chords={[q.prev, q.best]}
                  countIn={false}
                  label="hear the move"
                />
              </div>
            )}
            <Piano
              pressed={built}
              exactKeys={exactKeys}
              onPress={press}
              hint={answered ? [] : q.notes}
              disabled={answered}
            />
            <div className="ns-built">
              {keysList.length
                ? keysList
                    .sort((a, b) => a - b)
                    .map((m) => midiToKey(m).name)
                    .join(" · ")
                : "tap the notes — land close to where you are…"}
            </div>
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {!answered ? (
            <div className="foot">
              <button
                className="btn"
                disabled={invKeys.size < q.notes.length}
                onClick={checkLead}
              >
                Check
              </button>
            </div>
          ) : (
            <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
              <div className="verdict">
                <b>
                  {!lastCorrect
                    ? "not quite"
                    : leadInfo && leadInfo.smooth
                    ? "smooth"
                    : "right chord, wide path"}
                </b>
                <small>
                  {!lastCorrect
                    ? `${q.label} is ${q.notes.join("–")} — from ${q.prev.join(
                        "–"
                      )}, try ${q.best.join("–")}`
                    : leadInfo && leadInfo.travel != null
                    ? `you moved ${leadInfo.travel} semitone${
                        leadInfo.travel === 1 ? "" : "s"
                      }` +
                      (leadInfo.travel <= q.bestCost
                        ? " — that's the minimum"
                        : ` — the short path is ${q.bestCost} (${q.best.join("–")})`)
                    : `${q.label} — ${q.best.join("–")}`}
                </small>
              </div>
              <button
                className={`btn ${lastCorrect ? "" : "loje"}`}
                onClick={advance}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- play the progression: play each chord in turn on the keyboard.
  // Timed (q.timed): the groove plays and each chord must land in its bar,
  // near the downbeat — free-time otherwise. ----
  if (q.type === "playprog") {
    const playing = isTimed && timedPhase === "play" && !answered;
    const pass = playing && timedBar >= 0 ? Math.floor(timedBar / loopLen) : 0;
    const curStep = isTimed
      ? playing && timedBar >= 0
        ? timedBar % loopLen
        : -1
      : playStep;
    // the latest closed bar for a chart step (timed runs revisit each step)
    const markFor = (i) => {
      for (let b = marks.length - 1; b >= 0; b -= 1) {
        if (b % loopLen === i) return marks[b];
      }
      return null;
    };
    const hintNotes = answered
      ? []
      : isTimed
      ? q.steps[curStep >= 0 ? curStep : 0] || []
      : playTarget;
    return (
      <div className="ns-root">
        <div className="ns-app">
          {lessonBar}
          <div className="qarea">
            {capstoneTag}
            <div className="qkind">
              {isComp
                ? "comp the changes in time"
                : isTimed
                ? "play the changes in time"
                : q.resolve
                ? "tension, then release"
                : q.color
                ? "play the color changes"
                : q.voicing
                ? "comp the progression"
                : "play the progression"}
            </div>
            <div className="qprompt">
              {q.voicing ? "comp" : "play"} <b>{q.key}</b>{" "}
              <span className="muted">
                ({q.labels ? q.labels.join(" → ") : degStr(q.degrees)})
              </span>
            </div>
            {q.resolve && (
              <div className="qprompt-sub muted">
                hold the suspension, then let it settle into the triad
              </div>
            )}
            {q.voicing && (
              <div className="qprompt-sub muted">
                guide tones — skip the root, the bass has it
              </div>
            )}
            {isTimed && !answered && (
              <div className="qprompt-sub muted">
                {timedPhase !== "ready"
                  ? `pass ${Math.min(pass + 1, TIMED_PASSES)} of ${TIMED_PASSES} — the groove doesn't wait`
                  : isComp
                  ? `hold the ${compPattern.label} pattern through the changes, ${TIMED_PASSES}× around the loop`
                  : `one chord per bar, ${TIMED_PASSES}× around the loop — land each chord on its downbeat`}
              </div>
            )}
            {q.space && !answered && (
              <div className="qprompt-sub muted">
                when the chart says rest, hands off — the drummer fills, and
                silence grades as a hit
              </div>
            )}
            {isComp && !answered && (
              <div className="rt-strip">
                {Array.from({ length: 16 }, (_, i) => {
                  let cls = "rt-cell";
                  if (i % 4 === 0) cls += " beat";
                  if (compPattern.hits.includes(i)) cls += " exp";
                  return <span className={cls} key={i} />;
                })}
              </div>
            )}
            <div className="ns-prog">
              {q.degrees.map((d, i) => {
                const mark = isTimed ? markFor(i) : null;
                const done = !isTimed && (answered || i < playStep);
                const cur = isTimed
                  ? playing && i === curStep
                  : !answered && i === playStep;
                const markCls = mark
                  ? mark.onTime
                    ? " hit"
                    : mark.hit
                    ? " late"
                    : " miss"
                  : "";
                const stepLabel = q.labels ? q.labels[i] : chordFor(q.key, d);
                const stepInfo =
                  d === REST
                    ? null
                    : {
                        symbol: stepLabel,
                        label: q.labels ? null : chordLabel(stepLabel),
                        notes: q.steps && q.steps[i],
                      };
                return (
                  <ChordChip
                    key={i}
                    id={`prog-${qi}-${i}`}
                    info={stepInfo}
                    activeId={chipPopup && chipPopup.id}
                    onToggle={setChipPopup}
                    className={`ns-prog-step ${cur ? "current" : ""} ${done ? "done" : ""}${markCls}`}
                    emptyTag="div"
                  >
                    <span className="mono num">{d === REST ? "—" : d}</span>
                    <span className="chord">{d === REST ? "lay out" : stepLabel}</span>
                  </ChordChip>
                );
              })}
            </div>
            <Piano
              pressed={built}
              onPress={handleKeyInput}
              hint={hintNotes}
              disabled={answered}
            />
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {isTimed && !answered && (
            <div className="foot">
              {timedPhase === "ready" ? (
                <>
                  {isComp && (
                    <button className="btn ghost" onClick={previewPattern}>
                      Hear the pattern
                    </button>
                  )}
                  <button className="btn" onClick={startTimed}>
                    Start the groove
                  </button>
                </>
              ) : (
                <div className="verdict">
                  <small>four count-in clicks, then you're on</small>
                </div>
              )}
            </div>
          )}
          {answered && (
            <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
              <div className="verdict">
                <b>
                  {isComp
                    ? lastCorrect
                      ? "in the pocket"
                      : "the pattern slipped"
                    : isTimed
                    ? lastCorrect
                      ? "locked in"
                      : "keep chasing the beat"
                    : q.resolve
                    ? "released"
                    : q.voicing
                    ? "that's the comp"
                    : "that's the loop"}
                </b>
                <small>
                  {isComp && timedSummary
                    ? `${timedSummary.onTime}/${timedSummary.total} bars locked` +
                      (timedSummary.avgScore != null
                        ? ` · feel match ~${timedSummary.avgScore}%`
                        : "") +
                      (q.space
                        ? ` · rests kept ${marks.filter((m) => m.rest && m.hit).length}/${
                            marks.filter((m) => m.rest).length
                          }`
                        : "")
                    : isTimed && timedSummary
                    ? `${timedSummary.onTime}/${timedSummary.total} bars on the beat` +
                      (timedSummary.avgLatencyMs != null
                        ? ` · landed ~${timedSummary.avgLatencyMs}ms after the downbeat`
                        : "")
                    : `${q.key}: ${
                        q.labels
                          ? q.labels.join(" – ")
                          : q.degrees.map((d) => chordFor(q.key, d)).join(" – ")
                      }`}
                </small>
              </div>
              {isTimed && !lastCorrect && hearts > 0 && (
                <button className="btn ghost" onClick={retryTake}>
                  Another take
                </button>
              )}
              <button
                className={`btn ${lastCorrect ? "" : "loje"}`}
                onClick={advance}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- ear question: hear the loop, name it slot by slot ----
  if (q.type === "ear") {
    const checkEar = () => {
      const verdict = earSlotGrade(q.degrees, earPicks);
      setEarVerdict(verdict);
      // each slot exercised hearing that degree — partial credit feeds the
      // ear dimension of mastery even when the whole loop doesn't pass
      resultsRef.current.push(...earSlotResults(q.key, verdict.slots));
      grade(verdict.passed);
    };
    return (
      <div className="ns-root">
        <div className="ns-app">
          {lessonBar}
          <div className="qarea">
            {capstoneTag}
            <div className="qkind">listen — name the numbers, bar by bar</div>
            <div className="qsub">key of {q.key}</div>
            {CAN_PLAY && (
              <div className="ns-listen">
                <PlayButton
                  chords={chordsForProgression(q.key, q.degrees)}
                  big
                  label="play the loop"
                />
              </div>
            )}
            <EarSlots
              pool={q.pool}
              count={q.degrees.length}
              picks={earPicks}
              onPicks={setEarPicks}
              answered={answered}
              results={earVerdict && earVerdict.slots.map((s) => s.correct)}
            />
          </div>
          {!answered ? (
            <div className="foot">
              <button
                className="btn"
                disabled={earPicks.length < q.degrees.length}
                onClick={checkEar}
              >
                Check
              </button>
            </div>
          ) : (
            <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
              <div className="verdict">
                <b>
                  {!lastCorrect
                    ? "listen again"
                    : earVerdict && earVerdict.right === earVerdict.total
                    ? "perfect ears"
                    : "good ears"}
                </b>
                <small>
                  {earVerdict
                    ? `${earVerdict.right}/${earVerdict.total} bars — the loop was ${q.correct}`
                    : `the loop was ${q.correct}`}
                </small>
              </div>
              <button
                className={`btn ${lastCorrect ? "" : "loje"}`}
                onClick={advance}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- choice questions (translate / complete / progression / earmove / song) ----
  const isAudioQ = q.type === "earmove" || q.type === "song";
  const playChords =
    isAudioQ ||
    q.type === "progression" ||
    q.type === "complete" ||
    q.type === "tonic" ||
    q.type === "cadence" ||
    q.type === "common"
      ? chordsForProgression(q.key, q.degrees)
      : null;

  // Some questions accept more than one answer (a common-tone question where
  // the changes share two notes) — `accept` lists them all.
  const isRightChoice = (label) =>
    q.accept ? q.accept.includes(label) : label === q.correct;

  const renderChoice = (label) => {
    const isCorrect = isRightChoice(label);
    let cls = "choice";
    if (answered) {
      if (isCorrect) cls += " right";
      else if (label === selection) cls += " wrong";
      else cls += " dim";
    } else if (selection === label) {
      cls += " sel";
    }
    // Cadence options are prose labels ("authentic (V→I)"); everything else —
    // note names, chord symbols, number strings — reads better monospaced.
    const mono =
      q.type === "cadence" ? false : q.type !== "song" || q.ask === "numbers";
    return (
      <button
        key={label}
        className={cls}
        style={answered ? { pointerEvents: "none" } : undefined}
        onClick={() => !answered && setSelection(label)}
      >
        <span className={mono ? "mono" : ""}>{label}</span>
      </button>
    );
  };

  let head;
  if (q.type === "translate") {
    head = (
      <>
        <div className="qkind">
          {q.dir === "num2chord" ? "name the chord" : "name the number"}
        </div>
        <div className="qprompt">{q.prompt}</div>
      </>
    );
  } else if (q.type === "scalenote") {
    head = (
      <>
        <div className="qkind">the scale</div>
        <div className="qprompt">{q.prompt}</div>
      </>
    );
  } else if (q.type === "complete") {
    head = (
      <>
        <div className="qkind">complete the progression</div>
        <div className="qsub">key of {q.key}</div>
        <div className="ns-prog">
          {q.degrees.map((d, i) => {
            const isBlank = i === q.blank;
            const num = q.dir === "num" && isBlank ? "?" : d;
            const chord = q.dir === "chord" && isBlank ? "?" : chordFor(q.key, d);
            return (
              <div className={`ns-prog-step ${isBlank ? "blank" : ""}`} key={i}>
                <span className="mono num">{num}</span>
                <span className="chord">{chord}</span>
              </div>
            );
          })}
        </div>
        {CAN_PLAY && playChords && (
          <div className="ns-listen">
            <PlayButton chords={playChords} label="hear the loop" />
          </div>
        )}
      </>
    );
  } else if (q.type === "progression") {
    head = (
      <>
        <div className="qkind">spell the progression</div>
        <div className="qprompt">{q.prompt}</div>
        {CAN_PLAY && playChords && (
          <div className="ns-listen">
            <PlayButton chords={playChords} countIn={false} label="hear the answer" />
          </div>
        )}
      </>
    );
  } else if (q.type === "common") {
    head = (
      <>
        <div className="qkind">the common tone</div>
        <div className="qprompt">{q.prompt}</div>
        <div className="qprompt-sub muted">
          the note a smooth hand holds onto through the change
        </div>
        {CAN_PLAY && playChords && (
          <div className="ns-listen">
            <PlayButton chords={playChords} countIn={false} label="hear the change" />
          </div>
        )}
      </>
    );
  } else if (q.type === "earmove") {
    head = (
      <>
        <div className="qkind">hear the move</div>
        <div className="qsub">key of {q.key} — where did it land?</div>
        {CAN_PLAY && (
          <div className="ns-listen">
            <PlayButton chords={playChords} big label="play the move" />
          </div>
        )}
      </>
    );
  } else if (q.type === "tonic") {
    // Key is hidden on purpose — the whole point is hearing "home".
    head = (
      <>
        <div className="qkind">where's home?</div>
        <div className="qsub">hear it resolve, then pick the tonic</div>
        {CAN_PLAY && (
          <div className="ns-listen">
            <PlayButton chords={playChords} big label="play the cadence" />
          </div>
        )}
      </>
    );
  } else if (q.type === "cadence") {
    head = (
      <>
        <div className="qkind">name the cadence</div>
        <div className="qsub">key of {q.key} — hear the move</div>
        {CAN_PLAY && (
          <div className="ns-listen">
            <PlayButton chords={playChords} big label="play the move" />
          </div>
        )}
      </>
    );
  } else {
    // song
    head = (
      <>
        <div className="qkind">
          {q.ask === "title" ? "name that song" : "name the numbers"}
        </div>
        <div className="qsub">key of {q.key}</div>
        {CAN_PLAY && (
          <div className="ns-listen">
            <PlayButton chords={playChords} big label="play the changes" />
          </div>
        )}
      </>
    );
  }

  const verdictNote = () => {
    if (q.type === "translate" || q.type === "scalenote") {
      return `<b>${q.prompt}</b> &nbsp;→&nbsp; ${q.correct}`;
    }
    if (q.type === "complete") {
      return `<b>${q.correct}</b> completes ${q.key}: ${degStr(q.degrees)}`;
    }
    if (q.type === "progression") {
      return `<b>${q.key}: ${degStr(q.degrees)}</b> &nbsp;→&nbsp; ${q.correct}`;
    }
    if (q.type === "common") {
      return `<b>${q.chords[0]} → ${q.chords[1]}</b> keeps <b>${q.accept.join(
        " & "
      )}</b> — hold it, move the rest`;
    }
    if (q.type === "earmove") {
      return `${degStr(q.degrees)} — it landed on <b>${q.correct}</b> (${chordFor(
        q.key,
        q.correct
      )})`;
    }
    if (q.type === "tonic") {
      return `home in ${q.key} is <b>${q.correct}</b>`;
    }
    if (q.type === "cadence") {
      return `that move was a <b>${q.correct}</b>`;
    }
    return `<b>${q.songTitle}</b> — ${q.artist} · ${degStr(q.degrees)} in ${q.key}`;
  };

  return (
    <div className="ns-root">
      <div className="ns-app">
        {lessonBar}
        <div className="qarea">
          {capstoneTag}
          {head}
          <div className={`choices${q.options.length > 4 ? " many" : ""}`}>
            {q.options.map(renderChoice)}
          </div>
        </div>
        {!answered ? (
          <div className="foot">
            <button
              className="btn"
              disabled={!selection}
              onClick={() => grade(isRightChoice(selection))}
            >
              Check
            </button>
          </div>
        ) : (
          <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
            <div className="verdict">
              <b>{lastCorrect ? "yes!" : "not quite"}</b>
              <small>{lastCorrect ? "that's right" : `answer: ${q.correct}`}</small>
            </div>
            <Html className="notes" html={verdictNote()} />
            <button className={`btn ${lastCorrect ? "" : "loje"}`} onClick={advance}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonScreen;
